<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Similarity Profile' : 'Add Similarity Profile'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <!-- Safety Alert -->
        <div class="alert alert-info d-flex align-items-start gap-2 mb-4">
          <i class="material-symbols-outlined fs-20 text-info mt-1">shield</i>
          <div>
            <strong>Prinsip Keamanan Histori Order:</strong>
            <div class="fs-13 text-secondary mt-1">
              Perubahan pada profil ini hanya berlaku untuk pengecekan dan order berikutnya. Order terdahulu yang sudah terlanjur diproses tidak akan berubah status atau protokolnya.
            </div>
            <div v-if="isEditing && ordersCount > 0" class="mt-2 fw-semibold text-primary fs-13">
              <i class="material-symbols-outlined fs-16 align-middle">info</i>
              Profil ini sedang digunakan oleh {{ ordersCount }} order historis.
            </div>
          </div>
        </div>

        <form @submit.prevent="submitForm">
          <div class="row">
            <!-- Basic Identification -->
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Profile Code <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="form.profile_code"
                placeholder="e.g. JURNAL_STANDAR"
                @input="handleCodeInput"
                required
              />
              <small class="text-muted">Kode unik profil (huruf kapital dan garis bawah).</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Profile Name <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="form.profile_name"
                placeholder="e.g. Jurnal Ilmiah Standar (Sinta 2-3)"
                required
              />
              <small class="text-muted">Nama tampilan profil benchmark.</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Klasifikasi Dokumen <span class="text-danger">*</span></label>
              <select class="form-select" v-model="form.service_classification" required>
                <option value="Artikel Jurnal">Artikel Jurnal</option>
                <option value="JAD Dosen">JAD Dosen</option>
                <option value="Skripsi/Tesis/Disertasi">Skripsi/Tesis/Disertasi</option>
                <option value="Proposal">Proposal</option>
                <option value="Laporan">Laporan</option>
                <option value="Lainnya">Lainnya</option>
              </select>
              <small class="text-muted">Klasifikasi dokumen default yang ditautkan ke profil ini.</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4 d-flex align-items-center">
              <div class="form-check form-switch mt-3">
                <input class="form-check-input" type="checkbox" id="isActive" v-model="form.is_active" />
                <label class="form-check-label text-dark fs-14 fw-medium" for="isActive">
                  Profil Aktif (dapat dipilih untuk order baru)
                </label>
              </div>
            </div>

            <!-- Threshold Limits Section -->
            <div class="col-12 mb-3">
              <h6 class="fs-15 fw-semibold text-dark border-bottom pb-2 mb-3">
                <i class="material-symbols-outlined fs-18 text-primary align-middle me-1">speed</i>
                Batas Toleransi Similaritas (Thresholds)
              </h6>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Max Toleransi Overall (%) <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  class="form-control"
                  v-model.number="form.default_max_overall"
                  placeholder="20"
                  required
                />
                <span class="input-group-text bg-light text-muted">%</span>
              </div>
              <small class="text-muted">Batas maksimal persentase total similarity keseluruhan index.</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Max Toleransi Single Source (%) <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  class="form-control"
                  v-model.number="form.default_max_single_source"
                  placeholder="5"
                  required
                />
                <span class="input-group-text bg-light text-muted">%</span>
              </div>
              <small class="text-muted">Batas toleransi maksimal dari satu sumber internet/publikasi tunggal.</small>
            </div>

            <!-- Exclusions Section -->
            <div class="col-12 mb-3">
              <h6 class="fs-15 fw-semibold text-dark border-bottom pb-2 mb-3">
                <i class="material-symbols-outlined fs-18 text-primary align-middle me-1">tune</i>
                Pengaturan Filter Eksklusi Turnitin
              </h6>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <div class="form-check form-switch mt-2">
                <input class="form-check-input" type="checkbox" id="excludeBiblio" v-model="form.exclude_bibliography" />
                <label class="form-check-label text-dark fs-14 fw-medium" for="excludeBiblio">
                  Exclude Bibliography (Daftar Pustaka)
                </label>
              </div>
              <small class="text-muted d-block mt-1">Mengabaikan daftar pustaka / referensi saat pemeriksaan.</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <div class="form-check form-switch mt-2">
                <input class="form-check-input" type="checkbox" id="excludeQuotes" v-model="form.exclude_quotes" />
                <label class="form-check-label text-dark fs-14 fw-medium" for="excludeQuotes">
                  Exclude Quotes (Tanda Kutip)
                </label>
              </div>
              <small class="text-muted d-block mt-1">Mengabaikan teks kutipan langsung dalam tanda petik.</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Metode Filter Small Matches</label>
              <select class="form-select" v-model="form.small_matches_mode">
                <option value="none">None (Tidak Memfilter Kecocokan Kecil)</option>
                <option value="words">Words (Berdasarkan Jumlah Kata)</option>
                <option value="percentage">Percentage (Berdasarkan Persentase)</option>
              </select>
            </div>

            <div class="col-lg-6 col-md-6 mb-4" v-if="form.small_matches_mode !== 'none'">
              <label class="form-label text-dark fs-14 fw-medium">
                Nilai Ambang Small Matches {{ form.small_matches_mode === 'words' ? '(Jumlah Kata)' : '(%)' }}
              </label>
              <div class="input-group">
                <input
                  type="number"
                  min="1"
                  class="form-control"
                  v-model.number="form.small_matches_value"
                  :placeholder="form.small_matches_mode === 'words' ? '10' : '1'"
                  required
                />
                <span class="input-group-text bg-light text-muted">
                  {{ form.small_matches_mode === 'words' ? 'kata' : '%' }}
                </span>
              </div>
            </div>

            <!-- Description -->
            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Keterangan / Deskripsi Panduan</label>
              <textarea
                class="form-control"
                v-model="form.description"
                rows="3"
                placeholder="Catatan panduan pengecekan dokumen untuk tim verifikator/admin..."
              ></textarea>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-2">
            <RouterLink to="/similarity-profiles" class="btn btn-secondary fs-15 fw-medium">Batal</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Simpan Perubahan Profil' : 'Buat Profil Baru' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import similarityProfileService, { SimilarityProfile } from '@/services/similarityProfileService';

export default defineComponent({
  name: 'SimilarityProfileFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const isEditing = ref(false);
    const profileId = ref<number | null>(null);
    const ordersCount = ref(0);
    const loading = ref(false);

    const form = ref<SimilarityProfile>({
      profile_code: '',
      profile_name: '',
      service_classification: 'Artikel Jurnal',
      default_max_overall: 20,
      default_max_single_source: 5,
      exclude_bibliography: true,
      exclude_quotes: true,
      small_matches_mode: 'words',
      small_matches_value: 10,
      description: '',
      is_active: true
    });

    const handleCodeInput = (e: any) => {
      if (form.value.profile_code) {
        form.value.profile_code = form.value.profile_code.toUpperCase().replace(/\s+/g, '_');
      }
    };

    onMounted(async () => {
      if (route.params.id) {
        isEditing.value = true;
        profileId.value = Number(route.params.id);
        await fetchProfile(profileId.value);
      }
    });

    const fetchProfile = async (id: number) => {
      loading.value = true;
      try {
        const response = await similarityProfileService.getById(id);
        const data = response.data.data || response.data;
        ordersCount.value = data.orders_count || 0;
        form.value = {
          profile_code: data.profile_code || '',
          profile_name: data.profile_name || '',
          service_classification: data.service_classification || 'Artikel Jurnal',
          default_max_overall: Number(data.default_max_overall) || 0,
          default_max_single_source: Number(data.default_max_single_source) || 0,
          exclude_bibliography: !!data.exclude_bibliography,
          exclude_quotes: !!data.exclude_quotes,
          small_matches_mode: data.small_matches_mode || 'words',
          small_matches_value: data.small_matches_value !== null && data.small_matches_value !== undefined ? Number(data.small_matches_value) : 10,
          description: data.description || '',
          is_active: !!data.is_active
        };
      } catch (error) {
        console.error('Error fetching profile:', error);
        alert('Gagal mengambil data profil.');
        router.push('/similarity-profiles');
      } finally {
        loading.value = false;
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const payload: Partial<SimilarityProfile> = {
          ...form.value,
          small_matches_value: form.value.small_matches_mode === 'none' ? null : form.value.small_matches_value
        };

        if (isEditing.value && profileId.value) {
          await similarityProfileService.update(profileId.value, payload);
        } else {
          await similarityProfileService.create(payload);
        }
        router.push('/similarity-profiles');
      } catch (error: any) {
        console.error('Error saving profile:', error);
        const errMsg = error.response?.data?.message ||
          (error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join('\n') : 'Gagal menyimpan profil.');
        alert(errMsg);
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      ordersCount,
      loading,
      form,
      handleCodeInput,
      submitForm
    };
  }
});
</script>
